// AFBActivityCard.tsx
// Carte représentant une activité (existante ou nouvelle)

import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faCalendarAlt,
	faClock,
	faBriefcase,
	faFileInvoiceDollar,
	faHandshake,
	faMoon,
	faSave,
} from '@fortawesome/free-solid-svg-icons';

import { ActivityData } from 'features/activities';
import { isActivityDay } from 'features/activities/services/activities';
import { BadgeMovementData } from 'features/badgeMovement';
import AFBBadgeMovementCard from './AFBBadgeMovementCard';

/**
 * Statut front-end d'une activité.
 * Permettra plus tard :
 * - validation
 * - sauvegarde unitaire
 * - sauvegarde globale
 */
export type ActivityStatus = 'draft' | 'valid' | 'invalid' | 'saved';

interface Props {
	activity: ActivityData & { status?: ActivityStatus };
	projectOptions: { key: string; label: string }[];
	isSelected: boolean;

	onSelect: () => void;
	onProjectChange: (projectId: number, referenceNumber: string) => void;
	onRemoveMovement: (movement: BadgeMovementData) => void;
	onSave: (activity: ActivityData, force?: boolean) => void;
	onValidityChange?: (isValid: boolean) => void;
	onColorChange?: (
		color: 'green' | 'yellow' | 'orange' | 'red' | 'blue',
		activityId: number
	) => void;
}

const AFBActivityCard: React.FC<Props> = ({
	activity,
	projectOptions,
	isSelected,
	onSelect,
	onProjectChange,
	onRemoveMovement,
	onSave,
	onValidityChange,
	onColorChange,
}) => {
	// Formate une date au format "dd/MM/yyyy HH:mm:ss"
	const formatDateTime = (date?: Date | string) => {
		if (!date) return null;

		const d = new Date(date); // assure que c’est un Date
		const day = d.getDate().toString().padStart(2, '0');
		const month = (d.getMonth() + 1).toString().padStart(2, '0');
		const year = d.getFullYear();
		const hours = d.getHours().toString().padStart(2, '0');
		const minutes = d.getMinutes().toString().padStart(2, '0');
		const seconds = d.getSeconds().toString().padStart(2, '0');

		return (
			<>
				<strong>{`${day}/${month}/${year}`}</strong> {hours}:{minutes}:{seconds}
			</>
		);
	};

	/**
	 * Calcule la durée réelle de l'activité en minutes,
	 * c'est-à-dire la somme des durées de chaque segment IN→OUT parmi les mouvements de badge.
	 * On ignore les pauses entre une sortie et une rentrée.
	 * Si aucun mouvement n'est disponible, on tombe en fallback sur endDate - beginDate.
	 */
	const computeRealDurationMinutes = (): number | null => {
		const movements = activity.badgeMovements;

		// Fallback naïf si pas de mouvements
		if (!movements || movements.length === 0) {
			if (!activity.beginDate || !activity.endDate) return null;
			return (
				(new Date(activity.endDate).getTime() -
					new Date(activity.beginDate).getTime()) /
				1000 /
				60
			);
		}

		// On trie les mouvements par dateTime croissant
		const sorted = [...movements]
			.filter((m) => m.dateTime && m.direction)
			.sort(
				(a, b) =>
					new Date(a.dateTime as Date).getTime() -
					new Date(b.dateTime as Date).getTime()
			);

		// On apparie les IN→OUT et on somme les durées
		let totalMinutes = 0;
		let pendingIn: Date | null = null;

		for (const m of sorted) {
			const dt = new Date(m.dateTime as Date);
			if (m.direction === 'IN') {
				pendingIn = dt;
			} else if (m.direction === 'OUT' && pendingIn) {
				totalMinutes += (dt.getTime() - pendingIn.getTime()) / 1000 / 60;
				pendingIn = null;
			}
		}

		return totalMinutes > 0 ? totalMinutes : null;
	};

	/**
	 * Calcule la durée entre beginDate et endDate.
	 * Retourne couleur + texte formaté.
	 */
	let timeWarningLight = false;
	let timeWarning = false;
	let timeError = false;
	const renderDuration = () => {
		/*if (!activity.beginDate || !activity.endDate) return '-';

		const minutes =
			(new Date(activity.endDate).getTime() -
				new Date(activity.beginDate).getTime()) /
			1000 /
			60;*/

		// On utilise le temps réel (somme des segments IN→OUT) plutôt que le temps naïf (endDate - beginDate)
		const minutes = computeRealDurationMinutes();
		if (minutes === null) return '-';

		const hours = Math.floor(minutes / 60);
		const remainingMinutes = Math.round(minutes % 60);

		let color = 'inherit';

		// Règles de validation : entre 0h et 16h (0 et 960 minutes) : erreur
		if (minutes < 0 || minutes > 960) {
			color = 'red';
			timeError = true;
		} else if (minutes > 660) {
			// Avertissement plus de 11h (660 minutes)
			color = 'orange';
			timeWarning = true;
		} else if (minutes > 600) {
			// Avertissement plus de 10h (600 minutes)
			color = '#bbb100ff';
			timeWarningLight = true;
		}

		return (
			<span style={{ color }}>
				{hours}h{remainingMinutes.toString().padStart(2, '0')}
			</span>
		);
	};

	renderDuration();

	// Logique projet par défaut
	let projectId = '';
	let projectNotFound = false;
	let showDiff = false;

	if (activity.project) {
		// l'activité a déjà un projet assigné
		projectId = String(activity.project.id);
	} else if (activity.badgeMovements && activity.badgeMovements.length > 0) {
		// activité sans project mais avec mouvements
		// activity.badgeMovements[].project est un string | undefined
		const projectsFromMovements = activity.badgeMovements
			.map((m) => m.project) // array de string | undefined
			.filter((p): p is string => !!p); // on garde seulement les string non vides

		if (projectsFromMovements.length === 0) {
			projectId = '';
		} else {
			const firstProjectRef = projectsFromMovements[0];
			const allSame = projectsFromMovements.every((p) => p === firstProjectRef);

			// Si firstProjectRef commence exactement par un premier mot qui est sous la forme de 7 caractères
			// (une lettre, 5 chiffres et une lettre), alors on recupère que cette partie
			//const firstProjectFilteredRef =
			//	firstProjectRef.match(/^(\w{1}\d{5}\w{1})/)?.[0] || firstProjectRef;

			// Changement d'avis qui va remplacer ce qu'il y a ci-dessus :
			// dans tous les cas, on ne prend que le premier "mot" de projectRef,
			// quel qu'il soit, car c'est toujours une référence unique d'affaire
			// (même si elle ne suit pas le format classique)
			const firstProjectFilteredRef = firstProjectRef.split(' ')[0];

			if (allSame) {
				// chercher l'id correspondant dans projectOptions
				const project = projectOptions.find(
					(p) => p.label.split(' ')[0] === firstProjectFilteredRef
				);
				if (project) {
					projectId = project.key;
				} else {
					projectNotFound = true;
					projectId = '';
				}
			} else {
				showDiff = true;
			}
		}
	} else {
		projectId = '';
	}

	React.useEffect(() => {
		if (!activity.project && projectId) {
			const project = projectOptions.find((p) => p.key === projectId);
			if (project) {
				onProjectChange(Number(project.key), project.label);
			}
		}
	}, [projectId]);

	activity.isDay =
		activity.beginDate && activity.endDate
			? isActivityDay(new Date(activity.beginDate), new Date(activity.endDate))
			: undefined;

	const movements = activity.badgeMovements || [];

	const inCount = movements.filter((m) => m.direction === 'IN').length;
	const outCount = movements.filter((m) => m.direction === 'OUT').length;

	const hasValidDates =
		!!activity.beginDate &&
		!!activity.endDate &&
		new Date(activity.endDate) > new Date(activity.beginDate);

	const hasValidProject = !!projectId;
	const hasValidService = !!activity.service?.id;
	const hasBalancedMovements = inCount === outCount && inCount > 0;

	const isFullyValid =
		hasValidProject &&
		hasValidService &&
		hasBalancedMovements &&
		hasValidDates &&
		!timeError;

	const prevValidityRef = useRef<boolean | null>(null);

	useEffect(() => {
		if (onValidityChange && prevValidityRef.current !== isFullyValid) {
			onValidityChange(isFullyValid);
			prevValidityRef.current = isFullyValid;
		}
	}, [isFullyValid, onValidityChange]);

	useEffect(() => {
		let color: 'green' | 'yellow' | 'orange' | 'red' | 'blue' = 'green';

		if (!isFullyValid || timeError) color = 'red';
		else if (timeWarning) color = 'orange';
		else if (timeWarningLight) color = 'yellow';
		else if (activity.id && activity.id > 0) color = 'blue';
		else color = 'green';

		if (onColorChange) onColorChange(color, activity.id);
	}, [isFullyValid, timeError, timeWarning, timeWarningLight]);

	/**
	 * Détermine le style de la carte selon :
	 * - sélection
	 * - activité persistée ou nouvelle
	 */
	const getCardStyle = () => {
		let borderStyle = '2px dashed #00790a';
		let padding = 4;
		let backgroundColor = '#b2e9c5';

		if (timeWarningLight) {
			borderStyle = '2px dashed #bbb100ff';
			padding = 4;
			backgroundColor = '#f9f08dff';
		}

		if (timeWarning) {
			borderStyle = '2px dashed #f57c00';
			padding = 4;
			backgroundColor = '#feda9bff';
		}

		if (!isFullyValid) {
			borderStyle = '2px dashed red';
			padding = 4;
			backgroundColor = '#ffe6e6';
		}

		if (activity.id && activity.id > 0) {
			borderStyle = '2px solid #007bff';
			padding = 4;
			backgroundColor = '#e7f1ff';
		}

		if (isSelected) {
			borderStyle = '3px inset #d400ff';
			padding = 3;
		}

		return {
			border: borderStyle || '1px solid #ccc',
			padding: padding || 5,
			backgroundColor: backgroundColor || '#fff',
		};
	};
	// Rendu JSX
	return (
		<div
			style={{
				...getCardStyle(),
				marginBottom: 8,
				position: 'relative',
				cursor: 'pointer',
			}}
			onClick={onSelect}
		>
			{/* Badge "Nouveau" si id <= 0 */}
			{(!activity.id || activity.id <= 0) && (
				<>
					<div
						style={{
							position: 'absolute',
							top: 4,
							right: 8,
							backgroundColor: '#03b01d',
							color: '#fff',
							padding: '2px 4px',
							borderRadius: 4,
							fontSize: 12,
						}}
					>
						Nouveau
					</div>
					<div
						style={{
							position: 'absolute',
							bottom: 4,
							right: 8,
							cursor: isFullyValid ? 'pointer' : 'not-allowed',
							opacity: isFullyValid ? 1 : 0.4,
							color: isFullyValid ? '#0044ffff' : '#999',
						}}
						onClick={(e) => {
							e.stopPropagation();

							if (isFullyValid) {
								onSave(activity);
							} else {
								// Autoriser forçage si seules les dates sont invalides
								if (
									hasValidProject &&
									hasValidService &&
									hasBalancedMovements
								) {
									onSave(activity, true);
								}
							}
						}}
						title={
							isFullyValid
								? 'Sauvegarder'
								: 'Activité incomplète (affaire : ' +
									(activity.project ?? '?') +
									', service : ' +
									(activity.service?.label ?? '?') +
									', mouvements : ' +
									(hasBalancedMovements ? 'valides' : 'invalides') +
									')'
						}
					>
						<FontAwesomeIcon icon={faSave} />
					</div>
				</>
			)}

			{/* Ligne principale informations */}
			<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
				{/* Dates */}
				<span>
					<FontAwesomeIcon icon={activity.isDay ? faCalendarAlt : faMoon} />
					{' : '}
					{activity.beginDate ? formatDateTime(activity.beginDate) : '-'}
					{' → '}
					{activity.endDate ? formatDateTime(activity.endDate) : '-'}
				</span>

				{/* Durée */}
				<span>
					<FontAwesomeIcon icon={faClock} /> : {renderDuration()}
				</span>

				{/* Projet */}
				<span>
					<FontAwesomeIcon icon={faBriefcase} /> :
					{showDiff ? (
						<span style={{ color: 'red', fontWeight: 600 }}>DIFF !</span>
					) : (
						<select
							value={projectId}
							style={{
								border: projectNotFound ? '2px solid red' : undefined,
								backgroundColor: projectNotFound ? '#ffe6e6' : undefined,
							}}
							onChange={(e) => {
								const selectedKey = e.target.value;
								const project = projectOptions.find(
									(p) => p.key === selectedKey
								);
								if (!project) return;
								onProjectChange(Number(project.key), project.label);
							}}
							onClick={(e) => e.stopPropagation()}
						>
							<option value="">Sélectionner une affaire</option>
							{projectOptions.map((project) => (
								<option key={project.key} value={project.key}>
									{project.label}
								</option>
							))}
						</select>
					)}
				</span>

				{/* Facture */}
				<span>
					<FontAwesomeIcon icon={faFileInvoiceDollar} /> :{' '}
					{activity.customerInvoice
						? activity.customerInvoice.referenceNumber
						: '-'}
				</span>

				{/* Service */}
				<span>
					<FontAwesomeIcon icon={faHandshake} /> :{' '}
					{activity.service?.label
						? activity.service.label === 'ES non facturable'
							? 'ES non fact.'
							: activity.service.label
						: '-'}
				</span>
			</div>

			{/* Liste des mouvements */}
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: 8,
					marginTop: 8,
				}}
			>
				{activity.badgeMovements?.map((m) => (
					<AFBBadgeMovementCard
						key={m.id}
						movement={m}
						onRemove={() => onRemoveMovement(m)}
					/>
				))}
			</div>
		</div>
	);
};

export default AFBActivityCard;
